import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Send, X, MessageCircle } from "lucide-react";
import { contactAdmin } from "@/lib/api";

/**
 * In-app contact form modal.
 *
 * Replaces broken `mailto:` links (Chrome Android and Windows-without-mail-
 * client show "Qualsiasi attivazione" and do nothing). This posts to
 * `/api/contact` which forwards via the backend SMTP relay AND (for
 * category="access_request") registers the message in the /admin panel.
 *
 * Props:
 *   - open, onClose
 *   - category: "access_request" | "bug_report" | "credit_request_note" | "general"
 *   - defaultSubject: prefilled subject line
 *   - kicker: colored label at top (e.g. "Richiesta accesso")
 *   - onSent: callback after successful send
 */
export default function ContactModal({
  open,
  onClose,
  category = "general",
  defaultSubject = "",
  kicker,
  onSent,
}) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const submit = async () => {
    const s = subject.trim();
    const m = message.trim();
    if (!s || !m) {
      toast.error(t("contact.error_empty"));
      return;
    }
    if (sending) return;
    setSending(true);
    try {
      await contactAdmin({
        subject: s,
        message: m,
        category,
        meta_url: typeof window !== "undefined" ? window.location.href : "",
      });
      toast.success(t("contact.toast_sent"));
      onSent?.();
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(t("contact.toast_error", { msg: detail }));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="contact-modal"
    >
      <div
        className="max-w-lg w-full rounded-2xl border border-white/10 bg-[color:var(--bg-elev)] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center shadow-md">
            <MessageCircle size={18} className="text-white" />
          </div>
          <div>
            {kicker && (
              <div className="text-[10px] tiny-caps text-emerald-300 mb-0.5">
                {kicker}
              </div>
            )}
            <h3 className="text-lg font-bold text-white leading-none">
              {t("contact.modal_title")}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="ml-auto text-[color:var(--fg-muted)] hover:text-white"
            data-testid="contact-close"
            aria-label="close"
          >
            <X size={16} />
          </button>
        </div>

        <p className="text-xs text-[color:var(--fg-secondary)] mb-4 leading-relaxed">
          {t("contact.modal_body")}
        </p>

        <label className="block text-xs text-[color:var(--fg-muted)] mb-1">
          {t("contact.subject")}
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
          className="w-full mb-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="contact-subject"
          placeholder={t("contact.subject_placeholder")}
        />

        <label className="block text-xs text-[color:var(--fg-muted)] mb-1">
          {t("contact.message")}
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={4000}
          rows={6}
          className="w-full mb-4 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none"
          data-testid="contact-message"
          placeholder={t("contact.message_placeholder")}
        />

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-xs text-[color:var(--fg-secondary)] hover:text-white"
            data-testid="contact-cancel"
          >
            {t("contact.cancel")}
          </button>
          <button
            onClick={submit}
            disabled={sending}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30 disabled:opacity-50"
            data-testid="contact-submit"
          >
            <Send size={12} /> {t("contact.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
